/**
 * MultiselectFieldType — wielokrotny wybór z listy opcji. Storage: PG TEXT[].
 */

import { X } from 'lucide-react';

import type { FieldInputProps, FieldTypeDefinition } from '../FieldRegistry';

function MultiselectInput({ field, value, onChange, error, disabled }: FieldInputProps) {
  const options = field.options ?? [];
  const selected: string[] = Array.isArray(value) ? value.map(String) : [];

  const toggle = (val: string) => {
    if (selected.includes(val)) {
      onChange(selected.filter((v) => v !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  return (
    <div data-testid={`field-${field.slug}-input`} aria-invalid={!!error}>
      {selected.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {selected.map((val) => {
            const opt = options.find((o) => o.value === val);
            return (
              <span
                key={val}
                className="inline-flex items-center gap-1 rounded bg-accent-500/10 px-2 py-0.5 text-xs text-accent-600"
                style={
                  opt?.color ? { backgroundColor: `${opt.color}20`, color: opt.color } : undefined
                }
              >
                {opt?.label ?? val}
                <button
                  type="button"
                  onClick={() => toggle(val)}
                  disabled={disabled}
                  aria-label={`Usuń ${opt?.label ?? val}`}
                  className="hover:opacity-70"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap gap-1">
        {options
          .filter((o) => !selected.includes(o.value))
          .map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              disabled={disabled}
              className="rounded border border-border-primary px-2 py-0.5 text-xs text-text-secondary hover:bg-bg-tertiary disabled:opacity-50"
            >
              + {opt.label}
            </button>
          ))}
      </div>
    </div>
  );
}

export const MultiselectFieldType: FieldTypeDefinition = {
  type: 'multiselect',
  renderInput: (props) => <MultiselectInput {...props} />,
  renderCell: (field, value) => {
    if (!Array.isArray(value) || value.length === 0) return '';
    const labels = value.map((v) => {
      const opt = field.options?.find((o) => o.value === v);
      return opt?.label ?? String(v);
    });
    return labels.join(', ');
  },
  defaultValue: [],
};
